import type { CensusFigure, CensusRequirementFigure, CensusRow, CensusView, HppdFigures, StaffingRequirement } from '../types.js'

/**
 * The pure parts of the Census tool (census response plan §4): the dates it
 * shows, how a figure reads, and how a requirement is summarised. PPD is
 * patient days (a day's census is its PPD), HPPD hours per patient day.
 */

const DAY_MS = 24 * 60 * 60 * 1000
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] as const

export const CENSUS_DAYS_BACK = 14
export const CENSUS_DAYS_AHEAD = 28

export function addDays(date: string, days: number): string {
  return new Date(Date.parse(`${date}T00:00:00Z`) + days * DAY_MS).toISOString().slice(0, 10)
}

/** The range the tool reads: two weeks back to four ahead of today. */
export function censusRange(today: string): { from: string; to: string } {
  return { from: addDays(today, -CENSUS_DAYS_BACK), to: addDays(today, CENSUS_DAYS_AHEAD) }
}

export function weekdayOf(date: string): string {
  return WEEKDAYS[new Date(`${date}T00:00:00Z`).getUTCDay()] ?? ''
}

/** "Tue 10 Mar" for a grid row. */
export function shortDay(date: string): string {
  return new Date(`${date}T00:00:00Z`).toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' })
}

/** The row for one date and unit, if the view has it. */
export function rowFor(view: CensusView, date: string, unitId: string): CensusRow | null {
  return view.rows.find((row) => row.date === date && row.unitId === unitId) ?? null
}

/** "recorded", "forecast from 3 Tuesdays", "planned census", in words beside the number. */
export function describeBasis(figure: Pick<CensusFigure, 'basis' | 'sample' | 'kind'>, date: string): string {
  if (figure.basis === 'recorded') {
    return figure.kind === 'expected' ? 'expected' : 'recorded'
  }
  if (figure.basis === 'weekday_average') {
    return `forecast from ${figure.sample ?? 0} ${weekdayOf(date)}${(figure.sample ?? 0) === 1 ? '' : 's'}`
  }
  return 'planned census'
}

export function describeRequirementFigure(figure: CensusRequirementFigure | null, date: string): string {
  if (!figure) {
    return 'no count and nothing to forecast from'
  }
  if (figure.given) {
    return 'what if'
  }
  return describeBasis(figure, date)
}

/** "3.6 HPPD" or "—". */
export function formatHppd(figures: HppdFigures | null): string {
  return figures?.total === null || figures?.total === undefined ? '—' : `${figures.total.toFixed(2)} HPPD`
}

/** "6 CNA · 2 RN", or "10 CNA + a 4 h short shift 16:00–20:00" where the plan rounds a fraction into a short shift (F5). */
export function describeShiftSeats(shift: StaffingRequirement['shifts'][number]): string {
  const parts = shift.roles
    .filter((role) => role.seats > 0 || (role.shortSeats ?? 0) > 0)
    .map((role) => {
      const short = (role.shortSeats ?? 0) > 0 ? ` + a ${role.shortHours ?? shift.short?.hours ?? 0} h short shift${shift.short ? ` ${shift.short.start}–${shift.short.end}` : ''}` : ''
      return `${role.seats} ${role.role}${short}`
    })
  return parts.length === 0 ? 'nobody' : parts.join(' · ')
}

/** How the planned HPPD stands against the target, for the colour of the line. */
export function hppdStanding(requirement: StaffingRequirement): 'above' | 'below' | 'at' | 'none' {
  const target = requirement.hppd.target?.total ?? null
  const planned = requirement.hppd.planned?.total ?? null
  if (target === null || planned === null) {
    return 'none'
  }
  if (planned > target + 0.05) {
    return 'above'
  }
  return planned < target - 0.05 ? 'below' : 'at'
}

export const FLAG_TEXT: Record<StaffingRequirement['flags'][number], string> = {
  no_staffing_plan: 'No staffing plan: set one under Configuration › Scheduling to see what the census requires.',
  census_above_grid: 'The census is above the grid’s last band; the last band is used.',
  census_below_grid: 'The census is below the grid’s first band; the first band is used.',
  split_not_100: 'The HPPD shift split does not sum to 100 over the offered shift types.',
}
